import React, { useMemo, useState } from 'react';

function formatCurrency(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '-';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

// Display-side normalisation: old rows had "Auto-imported from Yahoo Finance"; show shortened version.
function shortNotes(raw) {
  if (!raw) return '-';
  if (raw.startsWith('Auto-imported')) return 'Auto-imported';
  return raw;
}

export default function DividendsPaginatedTable({ rows, reportCurrency, pageSize = 10 }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const visible = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safePage, pageSize]);

  return (
    <div style={{ overflowX: 'auto', marginTop: '16px' }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Ticker</th>
            <th style={thStyle}>Amount</th>
            <th style={thStyle}>Currency</th>
            <th style={thStyle}>Notes</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((tx) => {
            const txTicker = String(tx?.instrument?.ticker || tx?.ticker || '').toUpperCase() || '-';
            return (
              <tr key={tx.id}>
                <td style={tdStyle}>{formatDate(tx.occurredAt)}</td>
                <td style={tdStyle}>{txTicker}</td>
                <td style={{ ...tdStyle, color: '#22c55e', fontWeight: 700 }}>
                  +{formatCurrency(tx.netAmount || tx.grossAmount || 0, tx.tradeCurrency || reportCurrency)}
                </td>
                <td style={tdStyle}>{tx.tradeCurrency || '-'}</td>
                <td style={tdStyle}>{shortNotes(tx.notes)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div style={pagerStyle}>
          <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={pagerBtn}>‹</button>
          <span style={pagerInfo}>{safePage} / {totalPages}</span>
          <button type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={pagerBtn}>›</button>
        </div>
      )}
    </div>
  );
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '14px' };
const thStyle    = { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' };
const tdStyle    = { padding: '10px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)' };
const pagerStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '12px 0' };
const pagerBtn   = { width: '34px', height: '34px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface-strong)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '18px' };
const pagerInfo  = { color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 700 };
